import React, { useMemo, useState } from 'react';
import { Clock, ChevronDown, Pencil, AlertTriangle, Check, X, Minus, Plus, Link2, Building2 } from 'lucide-react';
import {
    PUBLICOS,
    FIDELIDADE_OPCOES,
    PlanoComercial,
    ModalidadePlano,
    classificarPublico,
    getFatorFidelidade,
    planosSelecionaveis,
} from '../lib/politicaComercial';
import {
    getComissaoInternoTabela,
    isDentroPoliticaInterno,
    INTERNO_TARIFAS_SELECIONAVEIS,
    INTERNO_BANDEIRAS,
} from '../lib/comissaoInterna';
import { resolverPlanoAtivo } from './PlanoMultiplicadoresTable';

/**
 * Condição comercial da UC em cartão, no lugar da matriz de multiplicadores
 * (planos × público) que ficava aberta na tela.
 *
 * A matriz existe para a VENDA: o multiplicador é da comissão do vendedor
 * externo. No cadastro da UC, quem abre quer saber que condição o cliente tem —
 * quanto de desconto na tarifa e na bandeira. Por isso o cartão mostra a
 * condição, e a comissão fica atrás de "ver detalhes".
 *
 * ⚠️ O modelo de comissão é POR MODALIDADE (a matriz não vale para todo mundo):
 *   - vendedor (externo): multiplicador = plano × público × fidelidade;
 *   - vendedor_interno:   percentual base da tabela do Comercial Interno, sem
 *                         público e sem multiplicador nesta tela (volume e bônus
 *                         só no fechamento mensal — ver comissaoInternaV1.ts);
 *   - parceiro:           não tem multiplicador; a comissão vem da rede.
 * Mostrar a matriz do externo para os outros dois é informação errada — era o
 * que acontecia com o Comercial Interno antes desta tela.
 *
 * Edição (set/2026): atalhos da política de cada modalidade (pacotes do externo,
 * degraus do interno) + ajuste fino de 1 em 1 para qualquer um. O ajuste fino
 * existe porque a base tem condições reais fora dos pacotes (23%, 27%, 33% de
 * tarifa...) que não havia como registrar. Sair da política não é barrado: o
 * cartão avisa, e com negociação vinculada o pedido de aprovação dispara ao
 * salvar (checkAndRequestDiscountApproval, no pai).
 */

/** Modalidade da política + "empresa parceira" (ex.: Gran), que só existe na UC:
 *  é quem vendeu, com a comissão saindo do split/deságio da empresa — sem
 *  multiplicador, como o parceiro pessoa física. */
export type ModalidadeVenda = ModalidadePlano | 'empresa_parceira';

interface PlanoResumoCardProps {
    modalidade: ModalidadeVenda;
    /** Percentuais em escala de exibição (ex.: 20, 50). */
    descTarifa?: number;
    descBandeira?: number;
    onChangeDescTarifa: (v: number) => void;
    onChangeDescBandeira: (v: number) => void;
    /** Volume (kWh) da negociação inteira — classifica o público do vendedor externo. */
    volumeKwh?: number;
    fidelidade?: string | null;
    /** "Fora da política" escolhido à mão (percentuais manuais no pai). */
    foraSelecionado?: boolean;
    /** Aplica os percentuais do pacote escolhido (vendedor externo). */
    onSelectPlano?: (plano: PlanoComercial) => void;
    /** Cadastro de UC nova: o plano é algo a ESCOLHER, não a conferir — o editor
     *  fica sempre aberto e o botão de abrir/fechar some. */
    modoCriacao?: boolean;
    /** Negociação de onde a modalidade e a fidelidade vêm. Com ela, as duas são só leitura. */
    negociacaoId?: number | null;
    /** Só na UC avulsa: permite escolher quem vendeu. */
    onChangeModalidade?: (m: ModalidadeVenda) => void;
    /** Só na UC avulsa: permite escolher a fidelidade. */
    onChangeFidelidade?: (f: string | null) => void;
    /** Empresa parceira da UC (a mesma "empresa faturadora" do cadastro). */
    empresaParceiraId?: string | null;
    /** Lista para escolher a empresa quando quem vendeu é "Empresa parceira".
     *  Só vem para quem pode definir a empresa faturadora (adm). */
    empresasParceiras?: { id: string; nome: string }[];
    onChangeEmpresaParceira?: (id: string | null) => void;
    /** Nome da empresa da UC, para o selo — independe de quem pode trocar. */
    empresaParceiraNome?: string | null;
}

const fmtMult = (v: number) => `${v.toFixed(2).replace('.', ',')}x`;
const fmtPct = (v: number) => `${Number(v).toFixed(1).replace('.', ',')}%`;

const MODALIDADE_LABEL: Record<ModalidadeVenda, string> = {
    vendedor: 'Vendedor externo',
    vendedor_interno: 'Closer e BDR',
    parceiro: 'Parceiro',
    empresa_parceira: 'Empresa parceira',
};
const MODALIDADES: ModalidadeVenda[] = ['vendedor', 'vendedor_interno', 'parceiro', 'empresa_parceira'];

/** Campo de percentual inteiro com −/+. Texto (não type="number"): o campo numérico
 *  do navegador zera no meio da digitação — ver FaturaCalculationModal. */
function AjustePercentual({ rotulo, valor, onChange }: { rotulo: string; valor: number; onChange: (v: number) => void }) {
    const [rascunho, setRascunho] = useState<string | null>(null);
    const limitar = (v: number) => Math.min(100, Math.max(0, Math.round(v)));
    return (
        <div>
            <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 dark:text-slate-500 mb-1">{rotulo}</p>
            <div className="flex items-stretch rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
                <button
                    type="button"
                    onClick={() => onChange(limitar(valor - 1))}
                    disabled={valor <= 0}
                    aria-label={`Diminuir ${rotulo.toLowerCase()} em 1%`}
                    className="px-3 text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-800 disabled:opacity-40 border-r border-gray-100 dark:border-slate-800"
                >
                    <Minus size={14} />
                </button>
                <div className="flex-1 flex items-center justify-center gap-0.5 py-1.5">
                    <input
                        type="text"
                        inputMode="numeric"
                        aria-label={rotulo}
                        value={rascunho ?? String(valor)}
                        onChange={e => {
                            const digitos = e.target.value.replace(/\D/g, '').slice(0, 3);
                            setRascunho(digitos);
                            if (digitos !== '') onChange(limitar(Number(digitos)));
                        }}
                        onBlur={() => setRascunho(null)}
                        className="w-12 text-center text-lg font-black text-gray-900 dark:text-white bg-transparent outline-none"
                    />
                    <span className="text-sm font-bold text-gray-400">%</span>
                </div>
                <button
                    type="button"
                    onClick={() => onChange(limitar(valor + 1))}
                    disabled={valor >= 100}
                    aria-label={`Aumentar ${rotulo.toLowerCase()} em 1%`}
                    className="px-3 text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-800 disabled:opacity-40 border-l border-gray-100 dark:border-slate-800"
                >
                    <Plus size={14} />
                </button>
            </div>
        </div>
    );
}

const PlanoResumoCard: React.FC<PlanoResumoCardProps> = ({
    modalidade,
    descTarifa,
    descBandeira,
    onChangeDescTarifa,
    onChangeDescBandeira,
    volumeKwh,
    fidelidade,
    foraSelecionado,
    onSelectPlano,
    modoCriacao = false,
    negociacaoId,
    onChangeModalidade,
    onChangeFidelidade,
    empresaParceiraId,
    empresasParceiras,
    onChangeEmpresaParceira,
    empresaParceiraNome,
}) => {
    const tarifa = Number(descTarifa) || 0;
    const bandeira = Number(descBandeira) || 0;
    const temCondicao = tarifa > 0 || bandeira > 0;

    const [editando, setEditando] = useState(false);
    const [verComissao, setVerComissao] = useState(false);
    // Derivado, não estado inicial: os percentuais chegam depois da montagem
    // (cadastro carregado, padrão aplicado), e um `useState(!temCondicao)` ficava
    // preso ao valor do primeiro render. UC sem condição mostra o editor porque
    // esconder o que falta preencher atrás de um botão é como se perde o desconto.
    const editorVisivel = modoCriacao || editando || !temCondicao;
    const mostraBotaoTrocar = !modoCriacao && temCondicao;

    const publico = useMemo(() => classificarPublico(Number(volumeKwh) || 0), [volumeKwh]);
    const planoAtual = useMemo(
        () => resolverPlanoAtivo(descTarifa, descBandeira, foraSelecionado),
        [descTarifa, descBandeira, foraSelecionado]
    );
    const planos = useMemo(
        () => planosSelecionaveis(planoAtual?.codigo).filter(p => !p.foraDaPolitica),
        [planoAtual]
    );

    const fatorFidelidade = getFatorFidelidade(fidelidade, planoAtual?.codigo);
    const fidelidadeLabel = FIDELIDADE_OPCOES.find(o => o.valor.toLowerCase() === (fidelidade || '').toLowerCase())?.label
        ?? (fidelidade || 'Isento de fidelidade');

    const ehExterno = modalidade === 'vendedor';
    const ehInterno = modalidade === 'vendedor_interno';
    // Os pacotes de plano são do vendedor externo. Interno e parceiro têm os mesmos
    // percentuais sem serem "o plano X": inferir aqui marcaria 20% + 0% bandeira do
    // Comercial Interno como o plano legado do externo.
    const planoExterno = ehExterno ? planoAtual : null;
    const multFinal = planoAtual ? Math.round(planoAtual.mult[publico] * fatorFidelidade * 100) / 100 : null;
    const foraPoliticaExterno = ehExterno && temCondicao && !!planoExterno?.foraDaPolitica;
    const foraPoliticaInterno = ehInterno && temCondicao && !isDentroPoliticaInterno(tarifa, bandeira);
    const foraDaPolitica = foraPoliticaExterno || foraPoliticaInterno;

    const titulo = !temCondicao
        ? 'Sem condição definida'
        : ehExterno && planoExterno && !planoExterno.foraDaPolitica
            ? planoExterno.label
            : `${tarifa}% + ${bandeira}% bandeira`;

    return (
        <div className="rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden">
            <div className="p-4 flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 dark:text-slate-500">Plano da unidade</p>
                    <p className={`text-lg font-bold leading-tight ${
                        !temCondicao ? 'text-amber-600 dark:text-amber-400'
                            : planoExterno?.legado ? 'text-red-700 dark:text-red-400'
                                : foraDaPolitica ? 'text-orange-600 dark:text-orange-400'
                                    : 'text-primary'
                    }`}>
                        {titulo}
                    </p>
                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                        {!onChangeModalidade && (
                            <span
                                className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-slate-300"
                                title={negociacaoId ? 'Definida na negociação — é ela que calcula a comissão de quem vendeu' : undefined}
                            >
                                {MODALIDADE_LABEL[modalidade]}
                                {negociacaoId ? (
                                    <span className="inline-flex items-center gap-0.5 font-normal opacity-80">
                                        · <Link2 size={10} /> negociação #{negociacaoId}
                                    </span>
                                ) : null}
                            </span>
                        )}
                        {/* Público (A/B/C) saiu do topo: só serve à comissão do externo e
                            continua em "ver detalhes da comissão". */}
                        {empresaParceiraNome && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-400"
                                title="Empresa parceira da UC — também a empresa faturadora">
                                <Building2 size={11} />
                                {empresaParceiraNome}
                            </span>
                        )}
                        {!onChangeFidelidade && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-400"
                                title={negociacaoId ? 'Fidelidade definida na negociação' : undefined}>
                                <Clock size={11} />
                                {fidelidadeLabel}
                            </span>
                        )}
                    </div>
                </div>
                {mostraBotaoTrocar && (
                    <button
                        type="button"
                        onClick={() => setEditando(v => !v)}
                        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-bold text-gray-600 dark:text-slate-300 hover:border-primary/40 hover:text-primary transition-colors"
                    >
                        {editando ? <><X size={13} /> Fechar</> : <><Pencil size={13} /> Trocar plano</>}
                    </button>
                )}
            </div>

            {/* Quem vendeu e fidelidade — só na UC avulsa. Com negociação, vêm dela (selos acima). */}
            {(onChangeModalidade || onChangeFidelidade) && (
                <div className="px-4 pb-4 -mt-1 flex flex-wrap items-end gap-x-6 gap-y-3">
                {onChangeModalidade && (
                <div>
                    <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 dark:text-slate-500 mb-1.5">Quem vendeu</p>
                    <div className="inline-flex flex-wrap rounded-xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/60 p-1 gap-1" role="radiogroup" aria-label="Quem vendeu">
                        {/* "Empresa parceira" pede a empresa — só aparece para quem pode
                            defini-la (adm), ou quando a UC já tem uma. */}
                        {MODALIDADES.filter(m => m !== 'empresa_parceira' || !!empresasParceiras?.length || !!empresaParceiraId).map(m => {
                            const atual = m === modalidade;
                            return (
                                <button
                                    key={m}
                                    type="button"
                                    role="radio"
                                    aria-checked={atual}
                                    onClick={() => onChangeModalidade(m)}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                                        atual
                                            ? 'bg-white dark:bg-slate-900 text-primary shadow-sm ring-1 ring-primary/30'
                                            : 'text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200'
                                    }`}
                                >
                                    {MODALIDADE_LABEL[m]}
                                </button>
                            );
                        })}
                    </div>
                </div>
                )}

                {onChangeModalidade && modalidade === 'empresa_parceira' && (
                    <div>
                        <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 dark:text-slate-500 mb-1.5">Qual empresa</p>
                        {empresasParceiras?.length && onChangeEmpresaParceira ? (
                            <select
                                value={empresaParceiraId || ''}
                                onChange={e => onChangeEmpresaParceira(e.target.value || null)}
                                aria-label="Empresa parceira"
                                className={`rounded-xl border px-3 py-2 text-xs font-bold bg-white dark:bg-slate-900 dark:text-slate-100 outline-none focus:border-primary ${
                                    empresaParceiraId ? 'border-gray-200 dark:border-slate-700' : 'border-orange-300 dark:border-orange-700'
                                }`}
                            >
                                <option value="">Selecione a empresa…</option>
                                {empresasParceiras.map(ep => <option key={ep.id} value={ep.id}>{ep.nome}</option>)}
                            </select>
                        ) : (
                            <p className="text-xs font-bold text-gray-700 dark:text-slate-200 py-2">{empresaParceiraNome || '—'}</p>
                        )}
                    </div>
                )}

                {onChangeFidelidade && (
                    <div>
                        <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 dark:text-slate-500 mb-1.5">Fidelidade</p>
                        <div className="inline-flex flex-wrap rounded-xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/60 p-1 gap-1" role="radiogroup" aria-label="Fidelidade">
                            {FIDELIDADE_OPCOES.map(o => {
                                // Sem fidelidade gravada = "Isento" (mesma leitura de getFatorFidelidade).
                                const atual = (fidelidade || 'Sem fidelidade').toLowerCase() === o.valor.toLowerCase();
                                return (
                                    <button
                                        key={o.valor}
                                        type="button"
                                        role="radio"
                                        aria-checked={atual}
                                        onClick={() => onChangeFidelidade(o.valor)}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                                            atual
                                                ? 'bg-white dark:bg-slate-900 text-violet-700 dark:text-violet-400 shadow-sm ring-1 ring-violet-300/60'
                                                : 'text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200'
                                        }`}
                                    >
                                        {o.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}
                </div>
            )}

            {/* Com o editor aberto, os campos já mostram os dois percentuais:
                repetir aqui em tamanho grande era a terceira vez na tela. */}
            {!editorVisivel && (
                <div className="grid grid-cols-2 divide-x divide-gray-100 dark:divide-slate-800 border-t border-gray-100 dark:border-slate-800">
                    <div className="px-4 py-3">
                        <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 dark:text-slate-500">Desconto na tarifa</p>
                        <p className="text-2xl font-black text-gray-900 dark:text-white">{`${tarifa}%`}</p>
                    </div>
                    <div className="px-4 py-3">
                        <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 dark:text-slate-500">Desconto na bandeira</p>
                        <p className="text-2xl font-black text-gray-900 dark:text-white">{`${bandeira}%`}</p>
                    </div>
                </div>
            )}

            {planoExterno?.legado && (
                <p className="flex items-start gap-1.5 px-4 py-2 text-[11px] font-semibold text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-t border-red-100 dark:border-red-900/40">
                    <AlertTriangle size={12} className="shrink-0 mt-0.5" />
                    Plano descontinuado — vale para este contrato, mas não para vendas novas.
                </p>
            )}
            {foraDaPolitica && (
                <p className="flex items-start gap-1.5 px-4 py-2 text-[11px] font-semibold text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 border-t border-orange-100 dark:border-orange-900/40">
                    <AlertTriangle size={12} className="shrink-0 mt-0.5" />
                    {negociacaoId
                        ? `Fora da política do ${MODALIDADE_LABEL[modalidade].toLowerCase()} — ao salvar, vai para aprovação do gerente.`
                        : `Fora da política do ${MODALIDADE_LABEL[modalidade].toLowerCase()}. UC sem negociação não passa por aprovação: confira antes de salvar.`}
                </p>
            )}

            {editorVisivel && (
                <div className="px-4 py-4 border-t border-gray-100 dark:border-slate-800 bg-gray-50/60 dark:bg-slate-800/40 space-y-4">
                    {/* Atalhos: o que a política da modalidade oferece pronto. */}
                    {ehExterno && onSelectPlano && (
                        <div>
                            <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 dark:text-slate-500 mb-2">
                                Planos da política
                            </p>
                            {/* Um bloco por plano, lado a lado: o desconto na tarifa é o que
                                diferencia os planos, então é ele que vem grande. */}
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2" role="radiogroup" aria-label="Plano da política comercial">
                                {planos.map(plano => {
                                    const atual = planoAtual?.codigo === plano.codigo;
                                    return (
                                        <button
                                            key={plano.codigo}
                                            type="button"
                                            role="radio"
                                            aria-checked={atual}
                                            disabled={plano.legado}
                                            onClick={() => onSelectPlano(plano)}
                                            title={plano.legado ? 'Plano descontinuado — mantido só para mostrar a condição já fechada' : plano.label}
                                            className={`relative rounded-xl border px-3 py-2.5 text-left transition-all ${
                                                plano.legado
                                                    ? 'border-red-200 dark:border-red-900/50 bg-red-50/60 dark:bg-red-900/10 cursor-not-allowed'
                                                    : atual
                                                        ? 'border-primary bg-primary text-white shadow-sm'
                                                        : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-primary/50 hover:bg-primary/5'
                                            }`}
                                        >
                                            {atual && <Check size={14} className="absolute top-2 right-2" />}
                                            <span className={`block text-[10px] font-bold uppercase tracking-wide ${
                                                plano.legado ? 'text-red-500' : atual ? 'text-white/80' : 'text-gray-400 dark:text-slate-500'
                                            }`}>
                                                Tarifa
                                            </span>
                                            <span className={`block text-2xl font-black leading-tight ${
                                                plano.legado ? 'text-red-700 dark:text-red-400' : atual ? 'text-white' : 'text-gray-800 dark:text-slate-100'
                                            }`}>
                                                {plano.descTarifa}%
                                            </span>
                                            <span className={`block text-xs font-semibold mt-0.5 ${
                                                plano.legado ? 'text-red-600/80' : atual ? 'text-white/90' : 'text-gray-500 dark:text-slate-400'
                                            }`}>
                                                + {plano.descBandeira}% bandeira
                                            </span>
                                            {plano.legado && (
                                                <span className="block text-[10px] font-bold text-red-600 dark:text-red-400 mt-1">Descontinuado</span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {ehInterno && (
                        <div className="space-y-2">
                            <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 dark:text-slate-500">
                                Tabela de Closer e BDR
                            </p>
                            <div className="flex flex-wrap items-center gap-1.5">
                                <span className="text-[11px] font-semibold text-gray-500 dark:text-slate-400 w-16">Tarifa</span>
                                {INTERNO_TARIFAS_SELECIONAVEIS.map(t => (
                                    <button
                                        key={t}
                                        type="button"
                                        onClick={() => onChangeDescTarifa(t)}
                                        className={`px-2.5 py-1 rounded-lg border text-xs font-mono font-bold transition-all ${
                                            tarifa === t
                                                ? 'border-primary bg-primary text-white'
                                                : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-gray-600 dark:text-slate-300 hover:border-primary/40'
                                        }`}
                                    >
                                        {t}%
                                    </button>
                                ))}
                            </div>
                            <div className="flex flex-wrap items-center gap-1.5">
                                <span className="text-[11px] font-semibold text-gray-500 dark:text-slate-400 w-16">Bandeira</span>
                                {INTERNO_BANDEIRAS.map(b => (
                                    <button
                                        key={b}
                                        type="button"
                                        onClick={() => onChangeDescBandeira(b)}
                                        className={`px-2.5 py-1 rounded-lg border text-xs font-mono font-bold transition-all ${
                                            bandeira === b
                                                ? 'border-primary bg-primary text-white'
                                                : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-gray-600 dark:text-slate-300 hover:border-primary/40'
                                        }`}
                                    >
                                        {b}%
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Ajuste fino: qualquer valor, de 1 em 1, em qualquer modalidade. */}
                    <div>
                        <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 dark:text-slate-500 mb-2">
                            {modalidade === 'parceiro' || modalidade === 'empresa_parceira' ? 'Descontos' : 'Ajuste fino (de 1 em 1)'}
                        </p>
                        <div className="grid grid-cols-2 gap-3 max-w-md">
                            <AjustePercentual rotulo="Tarifa" valor={tarifa} onChange={onChangeDescTarifa} />
                            <AjustePercentual rotulo="Bandeira" valor={bandeira} onChange={onChangeDescBandeira} />
                        </div>
                    </div>
                </div>
            )}

            <div className="border-t border-gray-100 dark:border-slate-800">
                <button
                    type="button"
                    onClick={() => setVerComissao(v => !v)}
                    className="w-full flex items-center justify-between gap-2 px-4 py-2 text-[11px] font-bold uppercase tracking-wide text-gray-400 dark:text-slate-500 hover:text-primary transition-colors"
                >
                    Ver detalhes da comissão
                    <ChevronDown size={14} className={`transition-transform ${verComissao ? 'rotate-180' : ''}`} />
                </button>
                {verComissao && (
                    <div className="px-4 pb-3 text-[11px] text-gray-600 dark:text-slate-300 space-y-1.5">
                        {ehExterno && planoExterno && (
                            <>
                                <p>
                                    Multiplicador desta UC:{' '}
                                    <span className="font-mono font-bold text-gray-800 dark:text-slate-100">{fmtMult(multFinal!)}</span>
                                    <span className="text-gray-400 dark:text-slate-500">
                                        {' '}({fmtMult(planoExterno.mult[publico])} {planoExterno.foraDaPolitica ? 'fora da política' : 'do plano'} no {PUBLICOS[publico].label} × {fmtMult(fatorFidelidade)} de fidelidade)
                                    </span>
                                </p>
                                {!planoExterno.foraDaPolitica && (
                                    <p className="text-gray-400 dark:text-slate-500">
                                        Mesmo plano nos outros públicos:{' '}
                                        {(['C', 'B', 'A'] as const).map(p => `${PUBLICOS[p].label} ${fmtMult(planoExterno.mult[p])}`).join(' · ')}.
                                    </p>
                                )}
                            </>
                        )}
                        {ehExterno && !planoExterno && (
                            <p className="text-amber-600 dark:text-amber-400 font-semibold">Escolha um plano para o multiplicador ser calculado.</p>
                        )}
                        {ehInterno && (
                            foraPoliticaInterno || !temCondicao ? (
                                <p className="text-orange-600 dark:text-orange-400 font-semibold">
                                    Condição fora da tabela de Closer e BDR — o percentual fica sujeito à aprovação do gerente.
                                </p>
                            ) : (
                                <>
                                    <p>
                                        Comissão base desta condição:{' '}
                                        <span className="font-mono font-bold text-gray-800 dark:text-slate-100">{fmtPct(getComissaoInternoTabela(tarifa, bandeira))}</span>
                                    </p>
                                    <p className="text-gray-400 dark:text-slate-500">
                                        A tabela de Closer e BDR não usa multiplicador por público. O valor final depende do volume batido
                                        no mês e dos bônus, apurados no fechamento mensal.
                                    </p>
                                </>
                            )
                        )}
                        {modalidade === 'empresa_parceira' && (
                            <p className="text-gray-400 dark:text-slate-500">
                                Empresa parceira não tem multiplicador por plano: a remuneração dela sai do split do boleto,
                                e o desconto do cliente não pode passar do deságio da empresa (o banco recusa acima disso).
                            </p>
                        )}
                        {modalidade === 'parceiro' && (
                            <p className="text-gray-400 dark:text-slate-500">
                                Parceiro não tem multiplicador por plano: a comissão vem das regras da rede de parceiros.
                            </p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PlanoResumoCard;
